import { Component, EventEmitter, HostListener, Input, OnInit, Output, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PageControl } from '@app-model/page-control';
import { Vehicle } from '@app-model/vehicle';
import { FastQuoteService } from '@app-services/fast-quote.service';
import { OpenService, VehicleFilter } from '@app-services/open.service';
import { ShelfService } from '@app-services/shelf.service';
import { SharedAnimations } from '@app-shared/animations/shared-animations';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MarketPlacePersonalizationUtils } from '@app-components/layouts/shopkeeper-layout/marketplace-personalization';
import { Shelf } from '@app-model/shelf';
import { OemDealerSelection } from '../vehicles-pages/cast-off-vehicles/oem-dealers-slider/oem-dealers-slider.component';

@Component({
  selector: 'app-shelf',
  templateUrl: './shelf.component.html',
  styleUrls: ['./shelf.component.scss'],
  animations: [SharedAnimations]
})
export class ShelfComponent implements OnInit {

  @Input('shelf') shelf: Shelf;
  @Output('submitVehicleFilter') submitVehicleFilter = new EventEmitter<VehicleFilter>();
  @Output('submitFastQuoteFilter') submitFastQuoteFilter = new EventEmitter<VehicleFilter>();

  readonly PC_DEVICES_WIDTH: number = 1200;
  readonly TABLET_DEVICES_WIDTH: number = 700;
  private readonly MOBILE_BREAKPOINT: number = 768;
  private readonly TRACK_GAP: number = 12;

  @ViewChild('shelfTrack') shelfTrack: ElementRef<HTMLElement>;



  vehicles: Vehicle[] = [];
  allVehicles: Vehicle[] = [];
  loading: boolean = true;
  nextFinal = false;
  prevFinal = true;
  cardsQuantity = [1, 2, 3, 4];
  noResult: boolean = false;
  maxPage: number = 3;
  actualPage: number = 0;
  pageSize: number = 4;

  isMobile: boolean = false;
  isDesktop: boolean = false;

  isMobileViewport: boolean = false;
  activeSlide: number = 0;

  safeHtmlContent: SafeHtml;

  constructor(
    private readonly shelfService: ShelfService,
    private router: Router,
    private openService: OpenService,
    private sanitizer: DomSanitizer,
    private elRef: ElementRef
  ) { 
    this.openService.getLayoutVars().subscribe(layoutVars => {
      if (layoutVars) {
        MarketPlacePersonalizationUtils.initCssVars(this.elRef, layoutVars);
      }
    });
  }

  ngOnInit() {
    this.isMobile = this.shelf.targets?.includes('MOBILE');
    this.isDesktop = this.shelf.targets?.includes('DESKTOP');

    this.safeHtmlContent = this.sanitizer.bypassSecurityTrustHtml(this.shelf.htmlContent);
    
    this.handleSearch();
  }

  get slideIndicators(): number[] {
    return Array.from({ length: this.allVehicles.length }, (_, index) => index);
  }

  onTrackScroll(event: Event): void {
    const track = event.target as HTMLElement;
    const firstItem = track.firstElementChild as HTMLElement;

    if (!firstItem) {
      return;
    }

    const step = firstItem.getBoundingClientRect().width + this.TRACK_GAP;
    const index = Math.round(track.scrollLeft / step);
    const lastIndex = Math.max(this.allVehicles.length - 1, 0);

    this.activeSlide = Math.min(Math.max(index, 0), lastIndex);
  }

  goToSlide(index: number): void {
    const track = this.shelfTrack?.nativeElement;
    const firstItem = track?.firstElementChild as HTMLElement;

    if (!track || !firstItem) {
      return;
    }

    const step = firstItem.getBoundingClientRect().width + this.TRACK_GAP;
    track.scrollTo({ left: index * step, behavior: 'smooth' });
    this.activeSlide = index;
  }

  /** Recalcula paginação e estados dos botões */
  private updatePagination(): void {
    const totalPages = Math.ceil(this.allVehicles.length / this.pageSize);
    this.maxPage = totalPages > 0 ? totalPages - 1 : 0;

    /* Garante que a página atual não ultrapasse o máximo */
    if (this.actualPage > this.maxPage) {
      this.actualPage = this.maxPage;
    }

    this.prevFinal = this.actualPage === 0;
    this.nextFinal = this.actualPage >= this.maxPage;
  }

  onPrevClick() {
    if (this.actualPage === 0) {
      return;
    }

    this.actualPage--;
    this.updatePagination();

    this.loading = true;
    setTimeout(() => {
      this.vehicles = this.allVehicles.slice(
        this.actualPage * this.pageSize,
        this.actualPage * this.pageSize + this.pageSize
      );
      this.loading = false;
    }, 300);
  }

  onNextClick() {
    if (this.actualPage === this.maxPage) {
      return;
    }

    this.actualPage++;
    this.updatePagination();

    this.loading = true;
    setTimeout(() => {
      this.vehicles = this.allVehicles.slice(
        this.actualPage * this.pageSize,
        this.actualPage * this.pageSize + this.pageSize
      );
      this.loading = false;
    }, 300);
  }

  clickSlider(event: OemDealerSelection): void {
    if (!event.id || !event.name) {
      return;
    }

    this.router.navigate(['arrematar', event.name.replace(/ /g, "-"), event.id]);
  }

  private handleSearch(): void {
    this.handleResponsiveScreens();
    this.loading = true;

    if (this.shelf.type === 'OEM_DEALER') {
      this.loading = false;
      return;
    }

    this.shelfService.searchShelfItems(this.shelf).subscribe(resp => {
      if (!resp || !resp.data || resp.data.length < 1) {
        this.noResult = true;
      } else {
        this.allVehicles = resp.data as any[];
        this.updatePagination();
        this.vehicles = this.allVehicles.slice(
          this.actualPage * this.pageSize,
          this.actualPage * this.pageSize + this.pageSize
        );
      }
      this.loading = false;
    });

    // if (this.shelf.type == 'VEHICLE') {
    //   this.openService.searchAuctions(this.shelf.vehicleFilter).subscribe(resp => {
    //     if(!resp.data || resp.data.length < 1){
    //       this.noResult = true;
    //     }else{
    //       this.allVehicles = resp.data;
    //       this.vehicles = this.allVehicles.slice(0, 4);
    //     }
    //     this.loading = false;
    //   })
    // }

    // if (this.shelf.type == 'FAST_QUOTE') {
    //   this.fastQuoteService.searchFastQuotes(this.shelf.fastQuoteFilter).subscribe(resp => {
    //     if(!resp.data || resp.data.length < 1){
    //       this.noResult = true;
    //     }else{
    //       this.allVehicles = resp.data as any[];
    //       this.vehicles = this.allVehicles.slice(0, 4);
    //     }
        
    //     this.loading = false;
    //   })
    // }

    // if (this.shelf.type == 'OEM_DEALER') {
    //   this.loading = false;
    // }
  }

  private handleResponsiveScreens(): void {
    const width = window.innerWidth;

    this.isMobileViewport = width <= this.MOBILE_BREAKPOINT;

    if (width < this.PC_DEVICES_WIDTH && width > this.TABLET_DEVICES_WIDTH) {
      this.cardsQuantity = [1, 2];
      this.pageSize = 2;
    } else if (width >= this.PC_DEVICES_WIDTH) {
      this.cardsQuantity = [1, 2, 3, 4];
      this.pageSize = 4;
    } else {
      this.cardsQuantity = [1];
      this.pageSize = 1;
    }

    /* Recalcula paginação depois de alterar pageSize */
    this.updatePagination();

    /* Atualiza vehicles mostrados de acordo com novo tamanho de página */
    this.vehicles = this.allVehicles.slice(
      this.actualPage * this.pageSize,
      this.actualPage * this.pageSize + this.pageSize
    );
  }

  seeAll(): void {
    if (this.shelf.type == 'VEHICLE') {
      const vehicleFilter = this.shelf.vehicleFilter;
      vehicleFilter.pageControl.pageSize = 24;
      vehicleFilter.pageControl.page = 0;

      this.submitVehicleFilter.emit(vehicleFilter);
    } else if (this.shelf.type == 'FAST_QUOTE') {
      const fastQuoteFilter = this.shelf.fastQuoteFilter;
      fastQuoteFilter.pageControl.pageSize = 24;
      fastQuoteFilter.pageControl.page = 0;

      this.submitFastQuoteFilter.emit(fastQuoteFilter);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.handleResponsiveScreens();
  }
}
